import React, { useState, useEffect, useCallback } from 'react';
import styled from 'styled-components';
import { useNavigate } from 'react-router-dom';
import { API_BASE_URL } from '../../config/api';
import { authHeaders } from '../../services/auth';
import ToastProvider, { useToast } from '../../components/ui/ToastProvider';
import Spinner from '../../components/ui/Spinner';
import StatCard from '../../components/ui/StatCard';
import Card from '../../components/ui/Card';
import { ChartCard, BarChart } from '../../components/ui/ChartCard';
import { cache } from '../../utils/cache';
import { adminTheme } from '../../theme-admin';

const DashboardGrid = styled.div`
  display: grid;
  gap: ${adminTheme.spacing.xl};
  animation: fadeIn 0.5s ease-out;
`;

const StatsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: ${adminTheme.spacing.lg};
`;

const ContentGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(400px, 1fr));
  gap: ${adminTheme.spacing.xl};
`;

const WelcomeBanner = styled.div`
  background: ${adminTheme.gradients.royal};
  color: white;
  padding: ${adminTheme.spacing['2xl']} ${adminTheme.spacing.xl};
  border-radius: ${adminTheme.borderRadius['2xl']};
  box-shadow: ${adminTheme.shadows.xl};
  position: relative;
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    top: -50%;
    right: -10%;
    width: 300px;
    height: 300px;
    background: radial-gradient(circle, rgba(255, 255, 255, 0.2) 0%, transparent 70%);
    border-radius: 50%;
  }

  h1 {
    font-family: ${adminTheme.fonts.heading};
    font-size: ${adminTheme.fontSizes['3xl']};
    font-weight: ${adminTheme.fontWeights.bold};
    margin: 0 0 ${adminTheme.spacing.sm};
  }

  p {
    font-size: ${adminTheme.fontSizes.lg};
    opacity: 0.9;
    margin: 0;
  }
`;

const QuickActions = styled.div`
  display: flex;
  gap: ${adminTheme.spacing.md};
  flex-wrap: wrap;
  margin-top: ${adminTheme.spacing.xl};
`;

const ActionButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: ${adminTheme.spacing.sm};
  background: ${adminTheme.colors.background.primary};
  color: ${adminTheme.colors.gray[900]};
  border: none;
  padding: ${adminTheme.spacing.md} ${adminTheme.spacing.lg};
  border-radius: ${adminTheme.borderRadius.xl};
  font-weight: ${adminTheme.fontWeights.semibold};
  cursor: pointer;
  transition: ${adminTheme.transitions.fast};
  box-shadow: ${adminTheme.shadows.md};

  &:hover {
    transform: translateY(-2px);
    box-shadow: ${adminTheme.shadows.lg};
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const ActivityList = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${adminTheme.spacing.md};
`;

const ActivityItem = styled.div`
  display: flex;
  align-items: flex-start;
  gap: ${adminTheme.spacing.md};
  padding: ${adminTheme.spacing.md};
  border-radius: ${adminTheme.borderRadius.lg};
  background: ${adminTheme.colors.gray[50]};
  transition: ${adminTheme.transitions.fast};

  &:hover {
    background: ${adminTheme.colors.gray[100]};
    transform: translateX(4px);
  }
`;

const ActivityIcon = styled.div`
  width: 40px;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: ${props => props.$gradient || adminTheme.gradients.primary};
  border-radius: ${adminTheme.borderRadius.lg};
  font-size: ${adminTheme.fontSizes.xl};
  flex-shrink: 0;
`;

const ActivityContent = styled.div`
  flex: 1;

  h4 {
    font-size: ${adminTheme.fontSizes.base};
    font-weight: ${adminTheme.fontWeights.semibold};
    color: ${adminTheme.colors.gray[900]};
    margin: 0 0 4px;
  }

  p {
    font-size: ${adminTheme.fontSizes.sm};
    color: ${adminTheme.colors.gray[600]};
    margin: 0;
  }
`;

const ActivityTime = styled.div`
  font-size: ${adminTheme.fontSizes.xs};
  color: ${adminTheme.colors.gray[500]};
  white-space: nowrap;
`;

function PageBody() {
  const toast = useToast();
  const navigate = useNavigate();
  const [busy, setBusy] = useState(false);
  const [stats, setStats] = useState({
    courses: { total: 0, published: 0, draft: 0 },
    blogs: { total: 0, published: 0, draft: 0 },
    testimonials: { total: 0, featured: 0 },
    users: { total: 0, active: 0, inactive: 0 },
    contacts: { total: 0, pending: 0, resolved: 0 },
    weeklyActivity: [],
    recentActivities: []
  });
  const [loading, setLoading] = useState(true);

  const loadStats = useCallback(async (forceRefresh = false) => {
    // Check cache first
    if (!forceRefresh) {
      const cached = cache.get('dashboard-stats', 2 * 60 * 1000); // 2 min cache for dashboard
      if (cached) {
        setStats(cached);
        setLoading(false);
        return;
      }
    }

    setLoading(true);
    try {
      const res = await fetch(`${API_BASE_URL}/stats/dashboard`, { 
        headers: authHeaders() 
      });
      
      if (res.ok) {
        const data = await res.json();
        const statsData = data.data?.stats || {
          courses: { total: 0, published: 0, draft: 0 },
          blogs: { total: 0, published: 0, draft: 0 },
          testimonials: { total: 0, featured: 0 },
          users: { total: 0, active: 0, inactive: 0 },
          contacts: { total: 0, pending: 0, resolved: 0 },
          weeklyActivity: [],
          recentActivities: []
        };
        setStats(statsData);
        cache.set('dashboard-stats', statsData);
      }
    } catch (error) {
      toast.error('Failed to load dashboard statistics');
    } finally {
      setLoading(false);
    }
  }, [toast]);

  useEffect(() => {
    loadStats();
  }, [loadStats]);

  const importSamples = async () => {
    setBusy(true);
    try {
      const res = await fetch(`${API_BASE_URL}/api/admin/import-samples`, { 
        method: 'POST', 
        headers: { 'Content-Type': 'application/json', ...authHeaders() } 
      });
      if (!res.ok) throw new Error('failed');
      const data = await res.json();
      toast.success(
        'Sample data imported successfully', 
        `Courses: ${data?.inserted?.courses || 0}, Blogs: ${data?.inserted?.blogs || 0}, Testimonials: ${data?.inserted?.testimonials || 0}`
      );
      cache.invalidate('dashboard-stats');
      loadStats(true); // Force refresh
    } catch (_) {
      toast.error('Failed to import sample data');
    } finally { 
      setBusy(false); 
    }
  };

  // Helper function to format relative time
  const getRelativeTime = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'just now';
    if (diffMins < 60) return `${diffMins} minute${diffMins > 1 ? 's' : ''} ago`;
    if (diffHours < 24) return `${diffHours} hour${diffHours > 1 ? 's' : ''} ago`;
    if (diffDays < 7) return `${diffDays} day${diffDays > 1 ? 's' : ''} ago`;
    return date.toLocaleDateString();
  };

  // Use real data from stats or fallback to empty
  const chartData = stats.weeklyActivity && stats.weeklyActivity.length > 0
    ? stats.weeklyActivity
    : [
        { label: 'Mon', value: 0 },
        { label: 'Tue', value: 0 },
        { label: 'Wed', value: 0 },
        { label: 'Thu', value: 0 },
        { label: 'Fri', value: 0 },
        { label: 'Sat', value: 0 },
        { label: 'Sun', value: 0 },
      ];

  const recentActivities = (stats.recentActivities || []).map(activity => ({
    icon: activity.icon,
    title: activity.title,
    desc: activity.description,
    time: getRelativeTime(activity.time),
    gradient: adminTheme.gradients[activity.gradient] || adminTheme.gradients.blue
  }));

  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', padding: adminTheme.spacing['3xl'] }}>
        <Spinner label="Loading dashboard..." />
      </div>
    );
  }

  return (
    <DashboardGrid>
      <WelcomeBanner>
        <h1>👋 Welcome back, Admin!</h1>
        <p>Here's what's happening with your platform today</p>
        <QuickActions>
          <ActionButton onClick={() => navigate('/admin/courses')}>
            <span>➕</span> Add Course
          </ActionButton>
          <ActionButton onClick={() => navigate('/admin/blogs')}>
            <span>✍️</span> Write Blog
          </ActionButton>
          <ActionButton onClick={() => navigate('/admin/hero-slides')}>
            <span>🎭</span> Manage Slides
          </ActionButton>
          <ActionButton onClick={importSamples} disabled={busy}>
            {busy ? '⏳' : '📦'} {busy ? 'Importing...' : 'Import Samples'}
          </ActionButton>
        </QuickActions>
      </WelcomeBanner>

      <StatsGrid>
        <StatCard
          icon="📚"
          title="Total Courses"
          value={stats.courses?.total || 0}
          subtitle={`${stats.courses?.published || 0} published`}
          trend={12}
          gradient={adminTheme.gradients.blue}
          delay="0s"
        />
        <StatCard
          icon="📝"
          title="Blog Posts"
          value={stats.blogs?.total || 0}
          subtitle={`${stats.blogs?.published || 0} published`}
          trend={8}
          gradient={adminTheme.gradients.purple}
          delay="0.1s"
        />
        <StatCard
          icon="💬"
          title="Testimonials"
          value={stats.testimonials?.total || 0}
          subtitle={`${stats.testimonials?.featured || 0} featured`}
          trend={15}
          gradient={adminTheme.gradients.pink}
          delay="0.2s"
        />
        <StatCard
          icon="👥"
          title="Users"
          value={stats.users?.total || 0}
          subtitle={`${stats.users?.active || 0} active`}
          trend={5}
          gradient={adminTheme.gradients.ocean}
          delay="0.3s"
        />
        <StatCard
          icon="✉️"
          title="Contact Forms"
          value={stats.contacts?.total || 0}
          subtitle={`${stats.contacts?.pending || 0} pending`}
          trend={stats.contacts?.pending || 0}
          gradient={adminTheme.gradients.success}
          delay="0.4s"
        />
      </StatsGrid>

      <ContentGrid>
        <ChartCard 
          title="Weekly Activity" 
          subtitle="User engagement over the past week"
        >
          <BarChart data={chartData} gradient={adminTheme.gradients.blue} />
        </ChartCard>

        <Card title="Recent Activity">
          <ActivityList>
            {recentActivities.map((activity, index) => (
              <ActivityItem key={index}>
                <ActivityIcon $gradient={activity.gradient}>
                  {activity.icon}
                </ActivityIcon>
                <ActivityContent>
                  <h4>{activity.title}</h4>
                  <p>{activity.desc}</p>
                </ActivityContent>
                <ActivityTime>{activity.time}</ActivityTime>
              </ActivityItem>
            ))}
          </ActivityList>
        </Card>
      </ContentGrid>
    </DashboardGrid>
  );
}

export default function AdminHome() {
  return (
    <ToastProvider>
      <PageBody />
    </ToastProvider>
  );
}
